import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/orgApi";
import {
  fetchOrganizations, formToPayload, resourceToForm, mrnOf,
  GENDERS, BIRTHSEX, RACE_CATEGORIES, ETHNICITY_CATEGORIES, GENDER_IDENTITIES,
  PRONOUNS, MARITAL_STATUSES, LANGUAGES, RELATIONSHIP_TYPES,
} from "@/lib/patientApi";
import { PAT } from "@/constants/testIds";

const BLANK = {
  ssn: "", family: "", given: "", middle: "", suffix: "",
  gender: "", birthDate: "", managingOrgId: "",
  mobile: "", homePhone: "", email: "",
  line1: "", line2: "", city: "", state: "", postalCode: "", country: "US",
  marital: "", language: "",
  race: "", ethnicity: "", birthsex: "", genderIdentity: "", pronouns: "",
  contactRel: "C", contactFamily: "", contactGiven: "", contactPhone: "",
  deceased: false, deceasedDate: "",
};

function F({ label, id, value, onChange, testId, placeholder, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm text-slate-700">{label}</Label>
      <Input id={id} type={type} data-testid={testId} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Sel({ label, value, onChange, testId, options, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-700">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder || "Select"} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.code} value={o.code}>{o.display}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function PatientForm({ open, onClose, editing, onSaved, contentClassName }) {
  const [f, setF] = useState(BLANK);
  const [orgs, setOrgs] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    fetchOrganizations().then((list) => {
      setOrgs(list);
      const base = editing ? resourceToForm(editing) : { ...BLANK };
      if (!base.managingOrgId && list.length === 1) base.managingOrgId = list[0].id;
      setF(base);
    });
  }, [open, editing]);

  const submit = async () => {
    if (!f.given.trim() && !f.family.trim()) return toast.error("A given or family name is required.");
    if (!f.gender) return toast.error("Administrative gender is required (FHIR 1..1).");
    if (!f.managingOrgId) return toast.error("Managing organization is required — a Patient must be managed by an Organization.");
    const payload = formToPayload(f, orgs);
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/patients/${editing.id}`, payload);
        toast.success("Patient updated.");
      } else {
        const { data } = await api.post("/patients", payload);
        toast.success(`Patient created — MRN ${mrnOf(data)}`);
      }
      onSaved();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail;
      toast.error(msg || "Failed to save patient.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`max-h-[92vh] max-w-3xl overflow-y-auto${contentClassName ? ` ${contentClassName}` : ""}`} data-testid={PAT.form}>
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle>{editing ? "Edit Patient" : "Register Patient"}</DialogTitle>
              <DialogDescription>FHIR R4 Patient · US Core profile</DialogDescription>
            </div>
            {editing && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">MRN (system-assigned)</div>
                <Badge data-testid={PAT.mrnBadge} className="mt-1 bg-slate-900 font-fhir text-sm text-teal-300 hover:bg-slate-900">
                  {mrnOf(editing)}
                </Badge>
              </div>
            )}
          </div>
        </DialogHeader>

        <Tabs defaultValue="demographics" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="demographics" data-testid={PAT.tabDemographics}>Demographics</TabsTrigger>
            <TabsTrigger value="contact" data-testid={PAT.tabContact}>Contact</TabsTrigger>
            <TabsTrigger value="identity" data-testid={PAT.tabIdentity}>Identity</TabsTrigger>
            <TabsTrigger value="emergency" data-testid={PAT.tabEmergency}>Emergency</TabsTrigger>
          </TabsList>

          <TabsContent value="demographics" className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <F label="Given name *" id="p-given" value={f.given} onChange={set("given")} testId={PAT.fGiven} placeholder="Jane" />
            <F label="Family name *" id="p-family" value={f.family} onChange={set("family")} testId={PAT.fFamily} placeholder="Doe" />
            <F label="Middle name" id="p-middle" value={f.middle} onChange={set("middle")} testId={PAT.fMiddle} />
            <F label="Suffix" id="p-suffix" value={f.suffix} onChange={set("suffix")} testId={PAT.fSuffix} placeholder="Jr" />
            <Sel label="Administrative gender *" value={f.gender} onChange={set("gender")} testId={PAT.fGender} options={GENDERS} />
            <F label="Date of birth" id="p-dob" value={f.birthDate} onChange={set("birthDate")} testId={PAT.fBirthDate} type="date" />
            <Sel label="Managing organization *" value={f.managingOrgId} onChange={set("managingOrgId")}
              testId={PAT.fOrg} options={orgs.map((o) => ({ code: o.id, display: o.name }))} placeholder="Select organization" />
            <F label="SSN (optional)" id="p-ssn" value={f.ssn} onChange={set("ssn")} testId={PAT.fSsn} placeholder="000-00-0000" />
          </TabsContent>

          <TabsContent value="contact" className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <F label="Mobile phone" id="p-mobile" value={f.mobile} onChange={set("mobile")} testId={PAT.fMobile} placeholder="+1-718-555-0199" />
            <F label="Home phone" id="p-home" value={f.homePhone} onChange={set("homePhone")} testId={PAT.fHomePhone} />
            <F label="Email" id="p-email" value={f.email} onChange={set("email")} testId={PAT.fEmail} placeholder="jane@example.com" />
            <div className="hidden sm:block" />
            <div className="sm:col-span-2"><F label="Address line 1" id="p-line1" value={f.line1} onChange={set("line1")} testId={PAT.fLine1} placeholder="25 Ocean Ave" /></div>
            <div className="sm:col-span-2"><F label="Address line 2" id="p-line2" value={f.line2} onChange={set("line2")} testId={PAT.fLine2} /></div>
            <F label="City" id="p-city" value={f.city} onChange={set("city")} testId={PAT.fCity} placeholder="Brooklyn" />
            <F label="State" id="p-state" value={f.state} onChange={set("state")} testId={PAT.fState} placeholder="NY" />
            <F label="Postal code" id="p-postal" value={f.postalCode} onChange={set("postalCode")} testId={PAT.fPostal} placeholder="11226" />
            <F label="Country" id="p-country" value={f.country} onChange={set("country")} testId={PAT.fCountry} />
          </TabsContent>

          <TabsContent value="identity" className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Sel label="Marital status" value={f.marital} onChange={set("marital")} testId={PAT.fMarital} options={MARITAL_STATUSES} />
            <Sel label="Preferred language" value={f.language} onChange={set("language")} testId={PAT.fLanguage} options={LANGUAGES} />
            <Sel label="Race (US Core)" value={f.race} onChange={set("race")} testId={PAT.fRace} options={RACE_CATEGORIES} />
            <Sel label="Ethnicity (US Core)" value={f.ethnicity} onChange={set("ethnicity")} testId={PAT.fEthnicity} options={ETHNICITY_CATEGORIES} />
            <Sel label="Birth sex (US Core)" value={f.birthsex} onChange={set("birthsex")} testId={PAT.fBirthsex} options={BIRTHSEX} />
            <Sel label="Gender identity" value={f.genderIdentity} onChange={set("genderIdentity")} testId={PAT.fGenderIdentity} options={GENDER_IDENTITIES} />
            <Sel label="Pronouns" value={f.pronouns} onChange={set("pronouns")} testId={PAT.fPronouns} options={PRONOUNS} />
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Deceased</Label>
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={f.deceased} onCheckedChange={set("deceased")} data-testid={PAT.fDeceased} />
                {f.deceased && (
                  <Input type="date" value={f.deceasedDate} data-testid={PAT.fDeceasedDate}
                    onChange={(e) => set("deceasedDate")(e.target.value)} className="max-w-[180px]" />
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="emergency" className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Sel label="Relationship" value={f.contactRel} onChange={set("contactRel")} testId={PAT.fContactRel} options={RELATIONSHIP_TYPES} />
            <div className="hidden sm:block" />
            <F label="Contact given name" id="p-cgiven" value={f.contactGiven} onChange={set("contactGiven")} testId={PAT.fContactGiven} placeholder="John" />
            <F label="Contact family name" id="p-cfamily" value={f.contactFamily} onChange={set("contactFamily")} testId={PAT.fContactFamily} placeholder="Doe" />
            <F label="Contact phone" id="p-cphone" value={f.contactPhone} onChange={set("contactPhone")} testId={PAT.fContactPhone} placeholder="+1-718-555-0200" />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} data-testid={PAT.cancelBtn}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid={PAT.saveBtn} className={contentClassName ? "emr-pat-form__save" : "bg-teal-700 hover:bg-teal-800"}>
            {saving ? "Saving…" : editing ? "Save changes" : "Register patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
